import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Box, Button, Flex, useToast } from "@chakra-ui/react";
import * as React from "react";
import {
  calculateDataTableState,
  constructQueryParamsString,
  getFormattedNumValue,
  getSortDirection,
  showErrorToast,
  translateSubElementString,
} from "../../../../utils";
import { useGetSuppliersYearlyData } from "../../../../hooks/query";
import { hideAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { DataTable } from "../../../../components/DataTable";
import CarbonIntensityGraph from "./CarbonIntensityGraph";
import DataTableV2 from "../../../../components/DataTable/DateTableV2";
import { MultiSelect } from "../../../../components";
import EmissionFilterComponent from "../EmissionFilterComponent";
import { getFilteredData } from "../../utils";
import { H1 } from "../../../../components/text/headings";
import LocalSpinner from "../../../../components/LocalSpinner";

const DEFAULT_YEARS = ["2021", "2022", "2023", "2024"];

const CarbonIntensityTab = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "suppliersEmissionsDashboard.emissionsByYear",
  });

  const [years, setYears] = useState(DEFAULT_YEARS);

  const { t: globalTranslation } = useTranslation("translation");
  const dispatch = useDispatch();
  const toast = useToast();
  const [yearByYearEmissionsData, setYearByYearEmissionsData] = useState([]);
  const [selectedDataQualityFilters, setSelectedDataQualityFilters] = useState([]);

  const getQueryParamsString = () => {
    const queryParams = {
      years: years.length !== 0 ? years : DEFAULT_YEARS,
    };
    return constructQueryParamsString(queryParams);
  };

  const { isFetching } = useGetSuppliersYearlyData(getQueryParamsString(), {
    onSuccess: (data) => {
      // exclude data where excludeFromReport is true
      const filteredData = data.filter((item) => !item.excludeFromReport);
      setYearByYearEmissionsData(filteredData);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
  });

  const filteredData = useMemo(() => {
    return getFilteredData(yearByYearEmissionsData, selectedDataQualityFilters);
  }, [yearByYearEmissionsData, selectedDataQualityFilters]);

  const dynamicEmissionsColumns = years.flatMap((year) => [
    {
      header: () => (
        <>
          {year} {translateSubElementString("suppliersEmissionsDashboard.emissionsByYear.gridHeaders.emissions")}
        </>
      ),
      accessorKey: `emissions-${year}`,
      size: 100,
      enableColumnFilter: false,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.yearlyEmissionData?.[year]?.totalCo2Emissions || 0;
        const b = rowB.original.yearlyEmissionData?.[year]?.totalCo2Emissions || 0;
        return a - b;
      },
      cell: ({ row }) => getFormattedNumValue(row.original.yearlyEmissionData?.[year]?.totalCo2Emissions || 0, 2),
    },
  ]);

  const dynamicIntensityColumns = years.flatMap((year) => [
    {
      header: `${year} ${t("gridHeaders.intensity")}`,
      accessorKey: `intensity${year}`,
      size: 100,
      enableColumnFilter: false,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.yearlyEmissionData?.[year]?.intensity || 0;
        const b = rowB.original.yearlyEmissionData?.[year]?.intensity || 0;
        return a - b;
      },
      cell: ({ row }) => {
        const intensity = getFormattedNumValue(row.original.yearlyEmissionData?.[year]?.intensity, 2);
        return isNaN(row.original.yearlyEmissionData?.[year]?.intensity) ? "N/A" : intensity;
      }, // Access nested data
    },
  ]);

  const columns = useMemo(() => {
    return [
      {
        header: t("gridHeaders.supplierName"),
        accessorKey: "supplierName",
        size: 120,
        enableSorting: true,
        sortingFn: "alphanumeric", // Use a predefined sorting function
      },
      ...dynamicEmissionsColumns,
      ...dynamicIntensityColumns,
    ];
  }, [dynamicIntensityColumns, dynamicEmissionsColumns, t]);

  if (isFetching) {
    return <LocalSpinner my={8} />;
  }

  return (
    <Flex flexFlow={"column"} gap={4} marginBlock={4}>
      <H1>
        Total CO<sub>2e</sub> Emissions per £m Gross
      </H1>
      <Flex direction="column" gap={4} mt={8}>
        <Flex align="center" gap={4}>
          <Box as="label" fontWeight="bold" fontSize="sm" color="gray.600">
            Year:
          </Box>
          <Box minW="250px">
            <MultiSelect
              onChange={(value) => setYears(value.map((item) => item.value))}
              options={Array.from({ length: 10 }, (_, i) => {
                const year = new Date().getFullYear() + 1 - i;
                return { label: year.toString(), value: year.toString() };
              })}
              isMulti={true}
              defaultValue={years.map((year) => ({ label: year, value: year }))}
            />
          </Box>
        </Flex>
        <EmissionFilterComponent
          type="dataQuality"
          selectedValues={selectedDataQualityFilters}
          onChange={(e) => setSelectedDataQualityFilters(e)}
        />
      </Flex>

      <CarbonIntensityGraph data={filteredData} years={years} />
      <DataTable
        data={filteredData}
        columns={columns}
        isLoading={isFetching}
        serverSidePagination={false}
        enableSorting={true}
        enableFilters={true}
      />
    </Flex>
  );
};

export default CarbonIntensityTab;
